import { randomUUID } from "crypto";

import { GoogleClassroomClient } from "@/services/GoogleClassroom";
import { GoogleOAuthClient } from "@/services/GoogleOAuth";
import { FirestoreClient } from "@/services/FirestoreClient";
import {
  ClassroomCourseWorkEntity,
  CourseWorkCreatedResult,
  CreateCourseWorkServiceArgs,
} from "@/types/services";

export class OakCourseWork {
  constructor(
    private readonly baseUrl: string,
    private readonly firestoreClient: FirestoreClient,
    private readonly vercelBypassSecret: string | undefined = process.env
      .VERCEL_AUTOMATION_BYPASS_SECRET,
    private readonly isStaging: boolean = process.env.VERCEL_ENV !==
      "production",
  ) {}

  /**
   * Creates a new CourseWork assignment with Link material, stores teacher
   * context in Firestore keyed by the opaque assignmentToken.
   *
   * The assignmentToken is generated before calling the Google API so it can
   * be embedded in the lesson URL. This avoids the circular dependency that
   * would arise from trying to include the courseWorkId (only known after
   * creation) in the URL, since materials cannot be patched post-creation.
   *
   * @param args - The coursework details including lesson slugs
   * @param oauthClient - An authenticated OAuth client for the teacher
   */
  public async createCourseWork(
    args: CreateCourseWorkServiceArgs,
    oauthClient: GoogleOAuthClient,
  ): Promise<CourseWorkCreatedResult & { assignmentToken: string }> {
    const assignmentToken = randomUUID();
    const lessonUrl = this.buildLessonUrl(args.lessonSlug, assignmentToken);

    const classroomClient = new GoogleClassroomClient(
      this.baseUrl,
      oauthClient,
    );
    const result = await classroomClient.createCourseWork(args.courseId, {
      title: args.title,
      description: args.description,
      lessonUrl,
      maxPoints: args.maxPoints,
    });

    const entity: ClassroomCourseWorkEntity = {
      assignmentToken,
      courseWorkId: result.courseWorkId,
      courseId: result.courseId,
      teacherLoginHint: args.teacherLoginHint,
      lessonSlug: args.lessonSlug,
      programmeSlug: args.programmeSlug,
      unitSlug: args.unitSlug,
      maxPoints: args.maxPoints,
      createdAt: new Date().toISOString(),
    };

    await this.firestoreClient.saveClassroomCourseWork(assignmentToken, entity);

    return { ...result, assignmentToken };
  }

  private buildLessonUrl(lessonSlug: string, assignmentToken: string): string {
    const path = `/pupils/lessons/${lessonSlug}/overview`;
    const params = new URLSearchParams({ assignmentToken });
    if (this.vercelBypassSecret && this.isStaging) {
      params.set("x-vercel-protection-bypass", this.vercelBypassSecret);
      params.set("x-vercel-set-bypass-cookie", "samesitenone");
    }
    return `${this.baseUrl}${path}?${params.toString()}`;
  }
}
